// Visionneuse de plan : fond (PDF ou image) + calque SVG des cheminements, de la calibration et du tracé en cours.
// Souris / doigt : clic pour poser un point, glisser une poignée pour déplacer un point. Clavier : curseur aux flèches, Entrée pour poser.
import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type PointerEvent } from 'react';
import { FondPlan } from './FondPlan.tsx';
import type { CheminementVue, PlanVue, Point } from './modele.ts';

export type Outil =
  | { type: 'consulter' }
  | { type: 'calibrer'; points: Point[] }
  | { type: 'tracer'; points: Point[]; prolonger: string | null };

export interface VisionneusePoignee { zoomer: (f: number) => void; ajuster: () => void }

interface Props {
  reperes?: { id: string; number: number; x: number; y: number; type_id: string }[];
  repereChoisi?: string | null;
  choisirRepere?: (id: string) => void;
  deplacerRepere?: (id: string, p: Point) => void;
  plan: PlanVue;
  cheminements: CheminementVue[];
  outil: Outil | { type: 'compter' };
  poser: (p: Point) => void;
  route: string | null;
  point: number | null;
  choisirRoute: (id: string | null) => void;
  choisirPoint: (i: number | null) => void;
  deplacerPoint: (routeId: string, i: number, p: Point, fin: boolean) => void;
  apercu: { route: string; points: Point[] } | null;
  lectureSeule: boolean;
  surZoom: (k: number) => void;
}

const ZMIN = 0.05; const ZMAX = 8;

export const VisionneusePlan = forwardRef<VisionneusePoignee, Props>(function VisionneusePlan(p, ref) {
  const cadre = useRef<HTMLDivElement>(null);
  const svg = useRef<SVGSVGElement>(null);
  const [taille, setTaille] = useState<{ l: number; h: number } | null>(null);
  const [k, setK] = useState(1);
  const [erreur, setErreur] = useState<string | null>(null);
  const [curseur, setCurseur] = useState<Point | null>(null);
  const centre = useRef<{ x: number; y: number } | null>(null);
  const glisse = useRef<{ route: string; i: number; bouge: boolean } | null>(null);
  const glisseRepere = useRef<{ id: string; depart: Point; dernier: Point | null } | null>(null);
  const [apercuRepere, setApercuRepere] = useState<{ id: string; p: Point } | null>(null);

  useEffect(() => { p.surZoom(k); }, [k]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setErreur(null); setTaille(null); }, [p.plan.id]);

  const changerZoom = useCallback((nk: number) => {
    const c = cadre.current;
    if (c) centre.current = { x: (c.scrollLeft + c.clientWidth / 2) / k, y: (c.scrollTop + c.clientHeight / 2) / k };
    setK(Math.min(ZMAX, Math.max(ZMIN, nk)));
  }, [k]);
  // Conserve le point central de la vue après un changement de zoom.
  useLayoutEffect(() => {
    const c = cadre.current; const m = centre.current;
    if (!c || !m) return;
    c.scrollLeft = m.x * k - c.clientWidth / 2; c.scrollTop = m.y * k - c.clientHeight / 2;
    centre.current = null;
  }, [k]);

  const ajuster = useCallback(() => {
    if (!taille || !cadre.current) return;
    const c = cadre.current;
    setK(Math.min(ZMAX, Math.max(ZMIN, Math.min((c.clientWidth - 2) / taille.l, Math.max(240, c.clientHeight - 2) / taille.h))));
  }, [taille]);
  useEffect(() => { if (taille) ajuster(); }, [taille]); // eslint-disable-line react-hooks/exhaustive-deps
  useImperativeHandle(ref, () => ({ zoomer: (f) => changerZoom(k * f), ajuster }), [changerZoom, k, ajuster]);

  // Ctrl + molette : zoom (la molette seule fait défiler le plan).
  useEffect(() => {
    const c = cadre.current;
    if (!c) return;
    const roue = (e: WheelEvent) => { if (!e.ctrlKey) return; e.preventDefault(); changerZoom(k * (e.deltaY < 0 ? 1.15 : 1 / 1.15)); };
    c.addEventListener('wheel', roue, { passive: false });
    return () => c.removeEventListener('wheel', roue);
  }, [changerZoom, k]);

  const versPlan = (cx: number, cy: number): Point => {
    const r = svg.current!.getBoundingClientRect();
    const x = ((cx - r.left) / r.width) * taille!.l; const y = ((cy - r.top) / r.height) * taille!.h;
    return [Math.round(Math.min(taille!.l, Math.max(0, x)) * 100) / 100, Math.round(Math.min(taille!.h, Math.max(0, y)) * 100) / 100];
  };

  const clic = (e: MouseEvent<SVGSVGElement>) => {
    if (!taille) return;
    if (p.outil.type === 'consulter') { p.choisirRoute(null); p.choisirPoint(null); return; }
    p.poser(versPlan(e.clientX, e.clientY));
  };

  const toucheCalque = (e: KeyboardEvent<SVGSVGElement>) => {
    if (!taille || p.outil.type === 'consulter' || e.target !== e.currentTarget) return;
    const c = curseur ?? [taille.l / 2, taille.h / 2];
    const pas = (e.shiftKey ? 50 : 5) / k;
    const d: Record<string, Point> = { ArrowLeft: [-pas, 0], ArrowRight: [pas, 0], ArrowUp: [0, -pas], ArrowDown: [0, pas] };
    if (d[e.key]) {
      e.preventDefault();
      setCurseur([Math.min(taille.l, Math.max(0, c[0] + d[e.key][0])), Math.min(taille.h, Math.max(0, c[1] + d[e.key][1]))]);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault(); p.poser([Math.round(c[0] * 100) / 100, Math.round(c[1] * 100) / 100]);
    }
  };

  // Poignées des points du cheminement choisi.
  const debutPoint = (e: PointerEvent, route: string, i: number) => {
    e.stopPropagation();
    p.choisirPoint(i);
    if (p.lectureSeule) return;
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    glisse.current = { route, i, bouge: false };
  };
  const mouvementPoint = (e: PointerEvent) => {
    const g = glisse.current; if (!g || !taille) return;
    g.bouge = true;
    p.deplacerPoint(g.route, g.i, versPlan(e.clientX, e.clientY), false);
  };
  const finPoint = (e: PointerEvent) => {
    const g = glisse.current; glisse.current = null;
    if (g?.bouge && taille) p.deplacerPoint(g.route, g.i, versPlan(e.clientX, e.clientY), true);
  };
  const touchePoint = (e: KeyboardEvent, route: string, i: number, pt: Point) => {
    if (p.lectureSeule) return;
    const pas = (e.shiftKey ? 20 : 2) / k;
    const d: Record<string, Point> = { ArrowLeft: [-pas, 0], ArrowRight: [pas, 0], ArrowUp: [0, -pas], ArrowDown: [0, pas] };
    if (d[e.key]) { e.preventDefault(); p.deplacerPoint(route, i, [Math.round((pt[0] + d[e.key][0]) * 100) / 100, Math.round((pt[1] + d[e.key][1]) * 100) / 100], true); }
  };

  const u = 1 / k; // une unité écran en unités du plan
  const ech = p.plan.echelle;
  const enCours = p.outil.type === 'calibrer' || p.outil.type === 'tracer' ? p.outil.points : [];
  const depart = p.outil.type === 'tracer' && p.outil.prolonger ? p.cheminements.find((c) => c.id === (p.outil as any).prolonger)?.points.at(-1) : undefined;
  const brouillon = depart ? [depart, ...enCours] : enCours;
  const pts = (l: Point[]) => l.map((q) => q.join(',')).join(' ');

  return (
    <div className="cch-plan-cadre" ref={cadre}>
      {erreur && <p className="cch-plan-erreur" role="alert">{erreur}</p>}
      <div className="cch-plan-feuille" style={taille ? { width: taille.l * k, height: taille.h * k } : undefined}>
        <FondPlan url={p.plan.fichier} mime={p.plan.piece?.mime ?? 'image/png'} page={p.plan.page} zoom={k}
          surTaille={setTaille} surErreur={setErreur} />
        {taille && (
          <svg ref={svg} className={`cch-calque cch-outil-${p.outil.type}`} viewBox={`0 0 ${taille.l} ${taille.h}`} width={taille.l * k} height={taille.h * k}
            style={p.reperes ? { touchAction: 'none' } : undefined}
            onClick={clic} tabIndex={0} onKeyDown={toucheCalque} onBlur={() => setCurseur(null)}
            onFocus={() => { if (p.outil.type !== 'consulter') setCurseur((c) => c ?? [taille.l / 2, taille.h / 2]); }}
            role="application" aria-roledescription="plan"
            aria-label={p.outil.type === 'consulter' ? `Plan ${p.plan.nom} et ses cheminements` : 'Plan : déplacez le curseur avec les flèches (Maj pour aller plus vite) et posez un point avec Entrée, ou cliquez sur le plan.'}>
            {ech.points && (
              <g className={`cch-calibration cch-calibration-${ech.statut}`}>
                <line x1={ech.points[0][0]} y1={ech.points[0][1]} x2={ech.points[1][0]} y2={ech.points[1][1]} vectorEffect="non-scaling-stroke" />
                {ech.points.map((q, i) => <circle key={i} cx={q[0]} cy={q[1]} r={5 * u} />)}
                <text x={(ech.points[0][0] + ech.points[1][0]) / 2} y={(ech.points[0][1] + ech.points[1][1]) / 2 - 8 * u} fontSize={13 * u} strokeWidth={3 * u} textAnchor="middle">
                  {ech.distanceM ? `${Number(ech.distanceM).toLocaleString('fr-FR')} m` : ''}
                </text>
              </g>
            )}
            {p.cheminements.map((c) => {
              const points = p.apercu?.route === c.id ? p.apercu.points : c.points;
              const choisi = p.route === c.id;
              return (
                <g key={c.id} className={`cch-route ${choisi ? 'est-choisi' : ''}`}>
                  <polyline points={pts(points)} className="cch-route-zone" vectorEffect="non-scaling-stroke"
                    onClick={(e) => { if (p.outil.type !== 'consulter') return; e.stopPropagation(); p.choisirRoute(c.id); p.choisirPoint(null); }} />
                  <polyline points={pts(points)} className="cch-route-trait" vectorEffect="non-scaling-stroke" />
                  {points[0] && <text x={points[0][0] + 8 * u} y={points[0][1] - 8 * u} fontSize={12 * u} strokeWidth={3 * u} className="cch-route-nom">{c.nom}</text>}
                  {choisi && p.outil.type === 'consulter' && points.map((q, i) => (
                    <g key={i} className={`cch-poignee-plan ${p.point === i ? 'est-choisi' : ''}`} tabIndex={0} role="button"
                      aria-label={`Point ${i + 1} sur ${points.length} du cheminement ${c.nom}${p.lectureSeule ? '' : ' ; flèches pour le déplacer'}`} aria-pressed={p.point === i}
                      onPointerDown={(e) => debutPoint(e, c.id, i)} onPointerMove={mouvementPoint} onPointerUp={finPoint} onPointerCancel={finPoint}
                      onClick={(e) => e.stopPropagation()} onFocus={() => p.choisirPoint(i)} onKeyDown={(e) => touchePoint(e, c.id, i, q)}>
                      <circle cx={q[0]} cy={q[1]} r={22 * u} className="cch-poignee-zone" />
                      <circle cx={q[0]} cy={q[1]} r={(p.point === i ? 8 : 6) * u} className="cch-poignee-point" />
                    </g>
                  ))}
                </g>
              );
            })}
            {(p.reperes ?? []).map((r) => {
              const q = apercuRepere?.id === r.id ? apercuRepere.p : [r.x, r.y];
              return <g key={r.id} role="button" tabIndex={0} aria-label={`Repère ${r.number}`} aria-pressed={p.repereChoisi === r.id}
                className={`cp-repere ${p.repereChoisi === r.id ? 'est-choisi' : ''}`} style={{ touchAction: 'none' }}
                onClick={(e) => { e.stopPropagation(); p.choisirRepere?.(r.id); }}
                onPointerDown={(e) => { e.stopPropagation(); p.choisirRepere?.(r.id); if (p.lectureSeule) return;
                  e.currentTarget.setPointerCapture(e.pointerId); glisseRepere.current = { id: r.id, depart: [e.clientX, e.clientY], dernier: null }; }}
                onPointerMove={(e) => { if (glisseRepere.current?.id !== r.id || !taille) return;
                  if (Math.hypot(e.clientX-glisseRepere.current.depart[0], e.clientY-glisseRepere.current.depart[1]) > 3) {
                    const point=versPlan(e.clientX,e.clientY);glisseRepere.current.dernier=point;setApercuRepere({ id: r.id, p: point }); } }}
                onPointerUp={() => { const glisse=glisseRepere.current;if(glisse?.id===r.id&&glisse.dernier)p.deplacerRepere?.(r.id,glisse.dernier);glisseRepere.current=null; setApercuRepere(null); }}
                onPointerCancel={() => { glisseRepere.current=null; setApercuRepere(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); p.choisirRepere?.(r.id); }
                  const delta: Record<string, Point> = { ArrowLeft:[-2*u,0],ArrowRight:[2*u,0],ArrowUp:[0,-2*u],ArrowDown:[0,2*u] };
                  if (!p.lectureSeule && delta[e.key]) { e.preventDefault(); p.deplacerRepere?.(r.id,[Math.max(0,Math.min(taille.l,r.x+delta[e.key][0])),Math.max(0,Math.min(taille.h,r.y+delta[e.key][1]))]); } }}>
                <circle cx={q[0]} cy={q[1]} r={22*u} className="cp-repere-cible" />
                <circle cx={q[0]} cy={q[1]} r={15*u} className="cp-repere-fond" />
                <text x={q[0]} y={q[1]+5*u} fontSize={14*u} textAnchor="middle" className="cp-repere-numero">{r.number}</text>
              </g>;
            })}
            {brouillon.length > 0 && (
              <g className={`cch-brouillon cch-brouillon-${p.outil.type}`}>
                <polyline points={pts(brouillon)} vectorEffect="non-scaling-stroke" />
                {enCours.map((q, i) => <circle key={i} cx={q[0]} cy={q[1]} r={6 * u} />)}
              </g>
            )}
            {curseur && p.outil.type !== 'consulter' && (
              <g className="cch-curseur" aria-hidden>
                <line x1={curseur[0] - 14 * u} y1={curseur[1]} x2={curseur[0] + 14 * u} y2={curseur[1]} vectorEffect="non-scaling-stroke" />
                <line x1={curseur[0]} y1={curseur[1] - 14 * u} x2={curseur[0]} y2={curseur[1] + 14 * u} vectorEffect="non-scaling-stroke" />
              </g>
            )}
          </svg>
        )}
      </div>
    </div>
  );
});
